import { useEffect, useRef, useState } from 'react';
import { Loader2, Mic } from 'lucide-react';
import { useRecorder } from '../hooks/useRecorder';
import { addNote, transcribeNote } from '../services/experiments';
import { cx } from './ui';

/** The hold-to-talk limit; the server refuses much more than this. */
const MAX_SECONDS = 120;
/** A press shorter than this is a tap, not a note. */
const MIN_HOLD_MS = 600;

/**
 * Hold, speak, release — the words appear as a note.
 *
 * Built like ENQI's push-to-talk: one clip per press, transcribed once by
 * ElevenLabs Scribe when the button is let go, kept word for word. The clip
 * itself is never stored.
 */
export default function HoldToTalk({
  projectId,
  experimentId,
  me,
  onText,
  label = 'Hold to talk',
}: {
  projectId: string;
  experimentId: string;
  me: { uid: string; name: string | null };
  /** When given, the words go here (into a note being edited) instead of a new note. */
  onText?: (text: string) => void;
  label?: string;
}) {
  // 32 kbps keeps two minutes well under the server's 3 MB cap; MP4 lets
  // iPhones record at all.
  const recorder = useRecorder({ allowMp4: true, bitsPerSecond: 32_000 });
  const [phase, setPhase] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const pressedAt = useRef(0);
  const held = useRef(false);

  async function begin() {
    setMessage(null);
    held.current = true;
    pressedAt.current = Date.now();
    const ok = await recorder.start();
    if (!ok) {
      held.current = false;
      return;
    }
    // Released while the microphone was still opening — usually the first
    // permission prompt. Throw that take away and say so.
    if (!held.current) {
      recorder.cancel();
      setMessage('Microphone ready. Hold the button while you speak.');
      return;
    }
    setPhase('recording');
  }

  async function finish() {
    if (!held.current) return;
    held.current = false;
    if (Date.now() - pressedAt.current < MIN_HOLD_MS) {
      recorder.cancel();
      setPhase('idle');
      setMessage('Hold the button down while you speak, then let go.');
      return;
    }
    const rec = await recorder.stop();
    if (!rec) {
      setPhase('idle');
      return;
    }
    setPhase('transcribing');
    try {
      const text = await transcribeNote(projectId, rec);
      if (onText) onText(text);
      else await addNote(projectId, experimentId, text, 'voice', me);
    } catch (e) {
      setMessage((e as Error)?.message ?? 'Could not transcribe that. Try again, or type it.');
    } finally {
      setPhase('idle');
    }
  }

  // Two minutes is the limit: stop and transcribe what there is.
  useEffect(() => {
    if (phase === 'recording' && recorder.seconds >= MAX_SECONDS) void finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.seconds, phase]);

  if (!recorder.supported) {
    return <p className="text-xs text-ink-400">This browser cannot record audio. Type the note instead.</p>;
  }

  const recording = phase === 'recording';
  const busy = phase === 'transcribing';

  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        disabled={busy}
        // Pointer capture keeps the release on this button even if a gloved
        // finger slides off it while talking.
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          void begin();
        }}
        onPointerUp={() => void finish()}
        onPointerCancel={() => void finish()}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if ((e.key === ' ' || e.key === 'Enter') && !e.repeat && !held.current) void begin();
        }}
        onKeyUp={(e) => {
          if (e.key === ' ' || e.key === 'Enter') void finish();
        }}
        className={cx(
          'flex w-full select-none items-center justify-center gap-2 rounded-xl border px-4 py-4 text-sm font-medium transition-colors touch-none',
          recording
            ? 'border-red-500 bg-red-950 text-red-100'
            : 'border-accent-600 bg-sky-950/60 text-sky-100 hover:bg-sky-950',
          busy && 'cursor-wait opacity-70',
        )}
        style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
        aria-label={label}
      >
        {busy ? <Loader2 size={20} className="animate-spin" /> : <Mic size={20} />}
        {busy
          ? 'Transcribing…'
          : recording
            ? `Listening… ${fmt(recorder.seconds)} — release to finish`
            : label}
      </button>
      {recording && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-800">
          <div
            className="h-full bg-red-400 transition-[width] duration-75"
            style={{ width: `${Math.min(100, Math.round(recorder.level * 140))}%` }}
          />
        </div>
      )}
      {(message || recorder.error) && (
        <p className="text-center text-xs text-amber-300">{message ?? recorder.error}</p>
      )}
    </div>
  );
}

function fmt(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
